import { Combobox, type ComboboxPropsInterface, Input } from '@zillow/constellation-10';
import { useState } from 'react';
import type { FocusEventHandler, ReactNode } from 'react';
import type { UseQueryResult } from '@zg-rentals/react-query5';
import type { RenterAddressAutocompleteWebResponseDto } from '@zg-rentals/ts-capps-client';
import { type AddressResponse, MIN_AUTOCOMPLETE_ADDRESS_LENGTH } from '../../hooks/useAddressAutocomplete';

export type AddressAutocompleteProps = Omit<
  ComboboxPropsInterface<string>,
  'onChange' | 'options' | 'renderEmptyState' | 'value'
> & {
  /** The stored form field value (e.g., street address) */
  value: string;
  /** Current partial address input value for autocomplete search */
  partialAddress: string;
  /** Function to update the partial address (from useAddressAutocomplete hook) */
  onSetPartialAddress: (value: string) => void;
  /** Query result from useAddressAutocomplete hook */
  queryData: UseQueryResult<RenterAddressAutocompleteWebResponseDto, unknown>;
  /** Callback when user selects an address from the dropdown */
  onAddressSelect: (address: AddressResponse) => void;
  /** Callback when user types in the input (for syncing with form state) */
  onInputChange?: (value: string) => void;
  disabled?: boolean;
  /** Whether the input should show error styling */
  error?: boolean;
  /** ID of the label element for accessibility */
  'aria-labelledby'?: string;
  renderEmptyState?: ReactNode;
  name?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};

export function AddressAutocomplete({
  value,
  partialAddress,
  onSetPartialAddress,
  queryData,
  onAddressSelect,
  onInputChange,
  renderEmptyState,
  disabled,
  error,
  'aria-labelledby': ariaLabelledby,
  onBlur,
  name,
  ...rest
}: AddressAutocompleteProps) {
  const [resetKey, setResetKey] = useState(0);
  const displayValue = value || partialAddress;

  return (
    <Combobox
      // Needed to reset the combobox when an option is selected otherwise if that same option gets
      // selected again it will not fire onOptionSelect
      key={resetKey}
      {...rest}
      options={
        queryData?.data?.addresses?.map((address) => ({
          ...address,
          value: address.name,
          label: address.name,
        })) ?? []
      }
      disabled={disabled}
      // @ts-expect-error - Combobox types don't properly support generic option types
      onOptionSelect={(selectedAddress: AddressResponse & { value: string; label: string }) => {
        onAddressSelect(selectedAddress);
        onSetPartialAddress('');
        setResetKey((k) => k + 1);
      }}
      loading={queryData.isFetching}
      size="sm"
      renderInput={({ ref, ...inputProps }) => {
        return (
          <Input
            {...inputProps}
            ref={ref as React.Ref<HTMLInputElement>}
            name={name}
            onBlur={(ev) => {
              inputProps.onBlur?.(ev);
              onBlur?.(ev);
            }}
            value={displayValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onSetPartialAddress(e.target.value);
              onInputChange?.(e.target.value);
            }}
            aria-labelledby={ariaLabelledby}
            error={error}
            size="sm"
            css={{ boxShadow: 'none', paddingLeft: 0 }}
          />
        );
      }}
      renderEmptyState={() =>
        partialAddress.length >= MIN_AUTOCOMPLETE_ADDRESS_LENGTH && queryData?.data ? (
          <Combobox.EmptyState>{renderEmptyState ? renderEmptyState : `No address found`}</Combobox.EmptyState>
        ) : null
      }
    />
  );
}
